import { useEffect, useState, useRef } from 'react';
import { QR_PLAY_DATAURI } from './qrPlay';
import { APP_STORE_URL } from './storeLinks';
import MarkaJenerik from '../../../components/brand/MarkaJenerik';

export default function DownloadCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="download" ref={sectionRef} className="relative py-20 overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#e77e23] to-[#d56d12]"></div>

      {/* Pattern Overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full">
          <svg width="100%" height="100%">
            <pattern id="dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="2" fill="white" />
            </pattern>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <MarkaJenerik className="mb-8" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Hemen İndir,<br />Mahalleni Keşfet!
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Komşularınla buluşmak için uygulamayı hemen indir ve mahallendeki fırsatları keşfet.
            </p>

            {/* App Store Buttons */}
            <div className="flex flex-wrap gap-4 mb-8">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-white text-[#111827] rounded-lg hover:bg-gray-100 transition-all cursor-pointer shadow-lg"
              >
                <i className="ri-apple-fill text-2xl mr-3"></i>
                <div className="text-left">
                  <div className="text-xs">Download on the</div>
                  <div className="text-sm font-semibold whitespace-nowrap">App Store</div>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.mahallebi.tr&hl=tr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-white text-[#111827] rounded-lg hover:bg-gray-100 transition-all cursor-pointer shadow-lg"
              >
                <i className="ri-google-play-fill text-2xl mr-3"></i>
                <div className="text-left">
                  <div className="text-xs">GET IT ON</div>
                  <div className="text-sm font-semibold whitespace-nowrap">Google Play</div>
                </div>
              </a>
            </div>

            {/* QR Code */}
            <div className="mt-8 inline-flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <a
                href="https://play.google.com/store/apps/details?id=com.mahallebi.tr&hl=tr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-24 h-24 bg-white rounded-lg flex items-center justify-center p-1.5 cursor-pointer"
                aria-label="Google Play QR kodu ile indir"
              >
                <img
                  src={QR_PLAY_DATAURI}
                  alt="Mahallebi'yi Google Play'den indir - QR kod"
                  className="w-full h-full"
                />
              </a>
              <div className="text-white">
                <p className="text-sm font-semibold mb-1">QR Kodu Tara</p>
                <p className="text-xs text-white/80">Hızlıca indir</p>
              </div>
            </div>
          </div>

          {/* Right Content - Phone Mockups */}
          <div
            className={`relative transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative flex items-center justify-center gap-3 sm:gap-6">
              {/* iPhone Mockup - Keşfet Sayfası */}
              <div className="relative transform -rotate-6 hover:rotate-0 transition-transform duration-500">
                <div className="w-44 h-[360px] sm:w-56 sm:h-[450px] lg:w-64 lg:h-[520px] bg-white rounded-[2rem] sm:rounded-[3rem] p-2 sm:p-3 shadow-2xl">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
                    <img
                      src="/images/download-kesfet.webp"
                      alt="Keşfet Sayfası"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* Android Mockup - Sohbet/Mesajlaşma */}
              <div className="relative transform rotate-6 hover:rotate-0 transition-transform duration-500">
                <div className="w-44 h-[360px] sm:w-56 sm:h-[450px] lg:w-64 lg:h-[520px] bg-gray-900 rounded-[2rem] sm:rounded-[3rem] p-2 sm:p-3 shadow-2xl">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
                    <img
                      src="/images/download-sohbet.webp"
                      alt="Mesajlaşma"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}